// src/pages/Batches/components/tables/BatchHistoryTab.tsx
import React, { useMemo, useState } from 'react';
import DefaultCard from '../../../../components/default/DefaultCard';
import DefaultDataTable from '../../../../components/default/DefaultDataTable';
import { customInterspersedStyles } from '../../../../utils/dataTableStyles';

interface BatchHistoryRow {
  id: number;
  comment: string;
  action_type: string;
  action_type_label?: string;
  quantity_movement?: number | null;
  quantity_movement_formatted?: string | null;
  batch_id: number;
  created_user_id: number | null;
  created_by?: string;
  created_by_name: string;
  created_at: string;
  created_at_formatted: string;
  is_inventory_movement?: boolean;
  movement_type?: string | null;
  movement_type_label?: string | null;
  parent_batch_id?: number | null;
  parent_batch_name?: string | null;
  delivery_date?: string | null;
  delivery_date_formatted?: string | null;
  product_name?: string | null;
  unit_name?: string | null;
  unit_price?: number | string | null;
  price_currency?: string | null;
  price_unit?: string | null;
  total_value?: number | string | null;
  total_value_currency?: string | null;
  purchase_order_id?: number | null;
  collection_order_id?: number | null;
  selection_order_id?: number | null;
  packing_order_id?: number | null;
  sales_order_id?: number | null;
}

interface Props {
  history: BatchHistoryRow[];
}

const cellLineStyle: React.CSSProperties = {
  whiteSpace: 'nowrap',
  fontSize: 'inherit',
};

const COL_WIDTH = '140px';
const COL_WIDTH_LONG = '220px';

function getOrderDisplay(row: BatchHistoryRow): string {
  if (row.purchase_order_id != null) return `#${row.purchase_order_id} Compra`;
  if (row.collection_order_id != null) return `#${row.collection_order_id} Recolección`;
  if (row.selection_order_id != null) return `#${row.selection_order_id} Selección`;
  if (row.packing_order_id != null) return `#${row.packing_order_id} Empaque`;
  if (row.sales_order_id != null) return `#${row.sales_order_id} Venta`;
  return '—';
}

const BatchHistoryTab: React.FC<Props> = ({ history }) => {
  const [filter, setFilter] = useState({ page: 1, per_page: 20 });

  const formatNumber = (value: number | string | null | undefined): string => {
    if (value === null || value === undefined || value === '' || value === 'None') return '-';
    const numValue = typeof value === 'string' ? parseFloat(value) : value;
    if (isNaN(numValue)) return '-';
    return numValue.toLocaleString('es-CL', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  };

  const formatCell = (content: React.ReactNode) => (
    <span style={cellLineStyle} className="text-muted">{content}</span>
  );

  const columns = useMemo(
    () => [
      {
        name: 'Fecha Registro',
        selector: (row: BatchHistoryRow) => row.created_at_formatted,
        sortable: false,
        minWidth: COL_WIDTH_LONG,
        width: COL_WIDTH_LONG,
        cell: (row: BatchHistoryRow) => formatCell(row.created_at_formatted),
      },
      {
        name: 'Producto',
        selector: (row: BatchHistoryRow) => row.product_name ?? '',
        sortable: false,
        minWidth: '280px',
        width: '280px',
        cell: (row: BatchHistoryRow) => formatCell(row.product_name ?? '—'),
      },
      {
        name: 'Cantidad',
        selector: (row: BatchHistoryRow) => row.quantity_movement ?? 0,
        sortable: false,
        width: COL_WIDTH,
        cell: (row: BatchHistoryRow) => {
          const q = row.quantity_movement;
          if (q === null || q === undefined)
            return formatCell('-');
          const isPos = q > 0;
          const icon = isPos ? 'mdi mdi-arrow-up-bold text-success' : 'mdi mdi-arrow-down-bold text-danger';
          const cls = isPos ? 'text-success fw-semibold' : 'text-danger fw-semibold';
          const text = row.quantity_movement_formatted ?? (q >= 0 ? `+${Number(q).toFixed(2)}` : Number(q).toFixed(2));
          return (
            <div className="d-flex align-items-center" style={cellLineStyle}>
              <i className={`${icon} me-1`}></i>
              <span className={cls}>{text}</span>
            </div>
          );
        },
      },
      {
        name: 'Unidad',
        selector: (row: BatchHistoryRow) => row.unit_name ?? '',
        sortable: false,
        width: COL_WIDTH,
        cell: (row: BatchHistoryRow) => formatCell(row.unit_name ?? '—'),
      },
      {
        name: 'Precio Unit.',
        selector: (row: BatchHistoryRow) => row.unit_price ?? '',
        sortable: false,
        width: COL_WIDTH,
        cell: (row: BatchHistoryRow) => {
          const v = row.unit_price;
          if (v === null || v === undefined || String(v) === '' || String(v) === 'None')
            return formatCell('-');
          return formatCell(formatNumber(v));
        },
      },
      {
        name: 'Unidad Precio',
        selector: (row: BatchHistoryRow) => (row.price_currency && row.price_unit ? `${row.price_currency}/${row.price_unit}` : row.price_currency ?? ''),
        sortable: false,
        width: COL_WIDTH,
        cell: (row: BatchHistoryRow) => {
          const t = row.price_currency && row.price_unit ? `${row.price_currency}/${row.price_unit}` : (row.price_currency ?? '—');
          return formatCell(t);
        },
      },
      {
        name: 'Valor Total',
        selector: (row: BatchHistoryRow) => row.total_value ?? '',
        sortable: false,
        width: COL_WIDTH,
        cell: (row: BatchHistoryRow) => {
          const v = row.total_value;
          if (v === null || v === undefined || String(v) === '' || String(v) === 'None')
            return formatCell('-');
          return formatCell(formatNumber(v));
        },
      },
      {
        name: 'Moneda',
        selector: (row: BatchHistoryRow) => row.total_value_currency ?? '',
        sortable: false,
        width: COL_WIDTH,
        cell: (row: BatchHistoryRow) => formatCell(row.total_value_currency ?? '—'),
      },
      {
        name: 'Tipo Mov.',
        selector: (row: BatchHistoryRow) => row.movement_type_label ?? row.action_type_label ?? row.action_type ?? '',
        sortable: false,
        minWidth: COL_WIDTH_LONG,
        width: COL_WIDTH_LONG,
        cell: (row: BatchHistoryRow) => {
          const isEntry = row.movement_type === 'ENTRY' || (row.quantity_movement != null && row.quantity_movement > 0);
          const isExit = row.movement_type === 'EXIT' || (row.quantity_movement != null && row.quantity_movement < 0);
          const cls = isEntry ? 'text-success fw-semibold' : isExit ? 'text-danger fw-semibold' : 'text-muted';
          const text = row.movement_type_label ?? row.action_type_label ?? row.action_type ?? '—';
          return <span className={cls} style={cellLineStyle}>{text}</span>;
        },
      },
      {
        name: 'Lote',
        selector: (row: BatchHistoryRow) => row.parent_batch_id ?? '',
        sortable: false,
        width: COL_WIDTH,
        cell: (row: BatchHistoryRow) => formatCell(row.parent_batch_id != null ? `#${row.parent_batch_id}` : '—'),
      },
      {
        name: 'Fecha Contabiliz.',
        selector: (row: BatchHistoryRow) => row.delivery_date_formatted ?? '',
        sortable: false,
        width: COL_WIDTH,
        cell: (row: BatchHistoryRow) => formatCell(row.delivery_date_formatted ?? '—'),
      },
      {
        name: 'Usuario',
        selector: (row: BatchHistoryRow) => row.created_by_name ?? '',
        sortable: false,
        minWidth: COL_WIDTH_LONG,
        width: COL_WIDTH_LONG,
        cell: (row: BatchHistoryRow) => formatCell(row.created_by_name),
      },
      {
        name: 'Orden',
        selector: (row: BatchHistoryRow) => getOrderDisplay(row),
        sortable: false,
        width: COL_WIDTH,
        cell: (row: BatchHistoryRow) => formatCell(getOrderDisplay(row)),
      },
    ],
    []
  );

  const paginatedData = useMemo(() => {
    const start = (filter.page - 1) * filter.per_page;
    return history.slice(start, start + filter.per_page);
  }, [history, filter.page, filter.per_page]);

  const tableMinWidth = 280 + 220 + 10 * 140 + 220 + 220;

  return (
    <DefaultCard>
      <h5 className="mb-3">Historial del lote</h5>

      {history.length === 0 ? (
        <div className="alert alert-info">
          <i className="mdi mdi-information-outline me-2"></i>
          No hay registros de historial para este lote.
        </div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <DefaultDataTable
            columns={columns}
            data={paginatedData}
            paginationTotalRows={history.length}
            filter={filter}
            setFilter={setFilter}
            progressPending={false}
            pagination={true}
            styleTable={{
              ...customInterspersedStyles,
              table: {
                style: {
                  ...(customInterspersedStyles?.table as any)?.style,
                  minWidth: tableMinWidth,
                },
              },
              cells: {
                style: {
                  ...(customInterspersedStyles?.cells as any)?.style,
                  fontSize: '0.875rem',
                  whiteSpace: 'nowrap',
                },
              },
              headCells: {
                style: {
                  ...(customInterspersedStyles?.headCells as any)?.style,
                  fontSize: '0.875rem',
                  whiteSpace: 'nowrap',
                },
              },
            }}
          />
        </div>
      )}
    </DefaultCard>
  );
};

export default BatchHistoryTab;
